import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ChevronDown, Menu, X, Search } from 'lucide-react';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    {
      label: 'Speakers/Guests',
      href: '/speakers',
      type: 'link'
    },
    {
      label: 'Experience',
      type: 'dropdown',
      items: [
        { label: 'Experience Overview', href: '/experience' },
        { label: 'Event Schedule', href: '/schedule' }
      ]
    },
    {
      label: 'Conference Info',
      type: 'dropdown',
      items: [
        { label: 'Attendee Guide', href: '/attendee-guide' },
        { label: 'Hotel & Travel', href: '/hotel-travel' },
        { label: 'ADA Assistance', href: '/ada-assistance' },
        { label: 'FAQs', href: '/faqs' }
      ]
    },
    {
      label: 'Get Involved',
      type: 'dropdown',
      items: [
        { label: 'Become a Sponsor', href: '/become-sponsor' },
        { label: 'Become a Vendor', href: '/become-vendor' },
        { label: 'Be a Volunteer', href: '/become-volunteer' }
      ]
    },
    {
      label: 'Community & Faith',
      type: 'dropdown',
      items: [
        { label: 'Join a Group', href: '/join-group' },
        { label: 'Get Baptized', href: '/get-baptized' },
        { label: 'Get a Discipler', href: '/get-discipler' },
        { label: 'Treasure Hunt', href: '/treasure-hunt' },
        { label: 'Group Chat / Newsletter', href: '/stay-connected' }
      ]
    },
    {
      label: 'Merch',
      href: '/merch',
      type: 'link'
    },
    {
      label: 'Badge Activation',
      href: '/badge-activation',
      type: 'link'
    }
  ];

  const handleDropdownToggle = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const handleMobileSubmenuToggle = (label) => {
    setOpenMobileSubmenu(openMobileSubmenu === label ? null : label);
  };

  const handleLinkClick = (href) => {
    setActiveSection(href.replace('#', ''));
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <header 
        className="fixed top-0 w-full z-[100] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.1)] transition-shadow duration-300"
        style={{ height: '80px' }}
      >
        <div className="max-w-[1440px] mx-auto px-6 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo - Left */}
            <a 
              href="/" 
              className="flex items-center h-10"
            >
              <div className="text-2xl font-bold text-[#0B1B3B] tracking-tight">
                KNOWN CON
              </div>
            </a>

            {/* Desktop Menu - Center */}
            <nav className="hidden xl:flex items-center gap-1">
              {navigationItems.map((item) => (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => item.type === 'dropdown' && setOpenDropdown(item.label)}
                  onMouseLeave={() => item.type === 'dropdown' && setOpenDropdown(null)}
                >
                  {item.type === 'link' ? (
                    <a
                      href={item.href}
                      onClick={() => handleLinkClick(item.href)}
                      className={`px-4 py-2 text-[15px] text-[#0B1B3B] hover:text-[#0B1B3B] transition-all duration-200 font-medium relative block whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 rounded ${
                        activeSection === item.href?.replace('#', '') ? 'font-bold' : ''
                      }`}
                    >
                      {item.label}
                      <span className={`absolute bottom-0 left-4 right-4 h-0.5 bg-[#CFA64B] transition-all duration-300 ${
                        activeSection === item.href?.replace('#', '') ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`} />
                    </a>
                  ) : (
                    <>
                      <button
                        className="px-4 py-2 text-[15px] text-[#0B1B3B] hover:text-[#0B1B3B] transition-all duration-200 font-medium flex items-center gap-1 relative whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 rounded"
                        onClick={() => handleDropdownToggle(item.label)}
                      >
                        {item.label}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                          openDropdown === item.label ? 'rotate-180' : ''
                        }`} />
                        <span className={`absolute bottom-0 left-4 right-4 h-0.5 bg-[#CFA64B] transition-all duration-300 opacity-0 group-hover:opacity-100`} />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {openDropdown === item.label && (
                        <div 
                          className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-[0_16px_32px_rgba(0,0,0,0.15)] py-2 min-w-[240px] animate-in fade-in slide-in-from-top-2 duration-150"
                          style={{ animationDuration: '120ms' }}
                        >
                          {item.items.map((subItem) => (
                            <a
                              key={subItem.label}
                              href={subItem.href}
                              onClick={() => handleLinkClick(subItem.href)}
                              className="block px-4 py-3 text-[15px] text-[#0B1B3B] hover:bg-[#F8F6F0] transition-all duration-200 font-medium relative group/item focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-inset"
                            >
                              {subItem.label}
                              <span className="absolute bottom-1 left-4 right-4 h-0.5 bg-[#CFA64B] opacity-0 group-hover/item:opacity-100 transition-opacity duration-200" />
                            </a>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side Actions - Desktop */}
            <div className="hidden xl:flex items-center gap-3">
              <button className="p-2.5 text-[#0B1B3B] hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6]">
                <Search className="w-5 h-5" />
              </button>
              <a href="/contact">
                <Button 
                  className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-semibold px-6 py-2.5 rounded-full shadow-md hover:shadow-[0_0_8px_rgba(207,166,75,0.4)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
                >
                  Contact Us
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="xl:hidden flex items-center gap-3">
              <button className="p-2 text-[#0B1B3B] hover:bg-gray-100 rounded-lg transition-colors">
                <Search className="w-5 h-5" />
              </button>
              <button
                className="p-2 text-[#0B1B3B] hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <div
        className={`fixed inset-0 z-[90] xl:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Drawer */}
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-300 overflow-y-auto ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ paddingBottom: '88px' }}
        >
          <div className="p-6">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-[#0B1B3B]">Menu</h2>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-[#0B1B3B] hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Mobile Navigation Items */}
            <nav className="space-y-2">
              {navigationItems.map((item) => (
                <div key={item.label}>
                  {item.type === 'link' ? (
                    <a
                      href={item.href}
                      className="block px-4 py-3 text-[15px] text-[#0B1B3B] hover:bg-[#F8F6F0] rounded-lg transition-all duration-200 font-medium min-h-[44px] flex items-center"
                      onClick={() => handleLinkClick(item.href)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <>
                      <button
                        className="w-full flex items-center justify-between px-4 py-3 text-[15px] text-[#0B1B3B] hover:bg-[#F8F6F0] rounded-lg transition-all duration-200 font-medium min-h-[44px]"
                        onClick={() => handleMobileSubmenuToggle(item.label)}
                      >
                        {item.label}
                        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${
                          openMobileSubmenu === item.label ? 'rotate-180' : ''
                        }`} />
                      </button>
                      
                      {/* Mobile Submenu */}
                      {openMobileSubmenu === item.label && (
                        <div className="mt-2 ml-4 space-y-1 border-l-2 border-[#CFA64B] pl-4">
                          {item.items.map((subItem) => (
                            <a
                              key={subItem.label}
                              href={subItem.href}
                              className="block px-4 py-2.5 text-[14px] text-[#0B1B3B] hover:bg-[#F8F6F0] rounded-lg transition-colors font-medium min-h-[44px] flex items-center"
                              onClick={() => handleLinkClick(subItem.href)}
                            >
                              {subItem.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Contact Us Button */}
      <div className="xl:hidden fixed bottom-0 left-0 right-0 z-[95] p-4 bg-white border-t border-gray-200 shadow-[0_-8px_24px_rgba(0,0,0,0.1)]">
        <a href="/contact" className="block">
          <Button className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-semibold px-6 py-4 rounded-full shadow-md text-lg min-h-[56px]">
            Contact Us
          </Button>
        </a>
      </div>
    </>
  );
};

export default Navigation;